// src/components/Finanzas/ErroresSincronizacion.jsx
// Vista de administración de sincronización contable. Lista comprobantes
// SYNC_ERROR y SYNC_NO_CONFIGURADO + acciones (forzar, marcar manual, flush).

import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Alert, Modal, Form } from 'react-bootstrap';
import { FaExclamationTriangle, FaSync, FaCheck, FaCog } from 'react-icons/fa';
import { Alertassw } from '../Alertas/Alertassw';
import {
  listarComprobantes,
  forzarSyncComprobante,
  marcarSincronizadoManual,
  flushNoConfigurados,
  historialSyncComprobante
} from '../../api/finanzas';
import { extractError } from './utils/extractError';

const formatoCOP = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`;

const ErroresSincronizacion = () => {
  const [items, setItems] = useState([]);
  const [filtroEstado, setFiltroEstado] = useState('SYNC_ERROR');
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);

  // Modal historial
  const [historial, setHistorial] = useState(null);
  const [showHist, setShowHist] = useState(false);

  // Modal marcar manual
  const [showManual, setShowManual] = useState(false);
  const [seleccionado, setSeleccionado] = useState(null);
  const [manualForm, setManualForm] = useState({ externalId: '', justificacion: '' });

  useEffect(() => {
    cargar();
  }, [filtroEstado]);

  const cargar = async () => {
    setLoading(true);
    try {
      const resp = await listarComprobantes({ syncStatus: filtroEstado });
      if (resp?.status === 200) setItems(resp.data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleForzar = async (id) => {
    setProcessing(id);
    try {
      await forzarSyncComprobante(id);
      Alertassw('success', 'Reintento disparado', 'Se reseteo el contador y se ejecuto sync.');
      cargar();
    } catch (err) {
      Alertassw('error', 'Error', extractError(err));
    } finally {
      setProcessing(null);
    }
  };

  const handleFlush = async () => {
    if (!window.confirm('¿Flushear todos los comprobantes SYNC_NO_CONFIGURADO?')) return;
    setProcessing('flush');
    try {
      const resp = await flushNoConfigurados();
      const r = resp?.data?.data || {};
      Alertassw('success', 'Flush completado', `Flusheados: ${r.totalFlusheados || 0}, errores: ${r.errores || 0}`);
      cargar();
    } catch (err) {
      Alertassw('error', 'Error', extractError(err));
    } finally {
      setProcessing(null);
    }
  };

  const handleVerHistorial = async (id) => {
    try {
      const resp = await historialSyncComprobante(id);
      if (resp?.status === 200) {
        setHistorial(resp.data.data);
        setShowHist(true);
      }
    } catch (err) {
      Alertassw('error', 'Error', err.message);
    }
  };

  const abrirManual = (comp) => {
    setSeleccionado(comp);
    setManualForm({ externalId: '', justificacion: '' });
    setShowManual(true);
  };

  const confirmarMarcarManual = async () => {
    if (!manualForm.externalId || !manualForm.justificacion) {
      Alertassw('warning', 'Campos requeridos', 'externalId y justificación son obligatorios');
      return;
    }
    setProcessing(seleccionado._id);
    try {
      await marcarSincronizadoManual(seleccionado._id, manualForm.externalId, manualForm.justificacion);
      Alertassw('success', 'Marcado manual', 'Comprobante marcado como SINCRONIZADO.');
      setShowManual(false);
      cargar();
    } catch (err) {
      Alertassw('error', 'Error', extractError(err));
    } finally {
      setProcessing(null);
    }
  };

  return (
    <Container fluid className="py-4 w-100">
      <Row className="mb-4">
        <Col>
          <h2 className="text-primary fw-bold">
            <FaExclamationTriangle className="me-2 text-warning" />
            Sincronización Contable
          </h2>
          <p className="text-muted">Administración de comprobantes con errores de sincronización</p>
        </Col>
      </Row>

      {/* Filtros */}
      <Card className="mb-3 shadow-sm w-100">
        <Card.Body className="w-100">
          <Row className="g-2 align-items-center">
            <Col md={4}>
              <Form.Label>Estado</Form.Label>
              <Form.Select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
                <option value="SYNC_ERROR">SYNC_ERROR</option>
                <option value="SYNC_NO_CONFIGURADO">SYNC_NO_CONFIGURADO</option>
                <option value="PENDIENTE_SYNC">PENDIENTE_SYNC</option>
                <option value="SINCRONIZANDO">SINCRONIZANDO</option>
                <option value="SINCRONIZADO">SINCRONIZADO</option>
              </Form.Select>
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <Button variant="outline-primary" onClick={cargar} className="w-100">Refrescar</Button>
            </Col>
            <Col md={5} className="d-flex align-items-end justify-content-end">
              {filtroEstado === 'SYNC_NO_CONFIGURADO' && (
                <Button variant="warning" onClick={handleFlush} disabled={processing === 'flush'}>
                  <FaCog className="me-1" />
                  {processing === 'flush' ? 'Procesando...' : 'Flush todos los SYNC_NO_CONFIGURADO'}
                </Button>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Tabla */}
      <Card className="shadow-sm w-100">
        <Card.Body className="w-100">
          {loading ? (
            <div className="text-center py-4"><div className="spinner-border text-primary" /></div>
          ) : items.length === 0 ? (
            <Alert variant="info" className="text-center mb-0">
              <FaCheck className="me-2 text-success" />
              No hay comprobantes con estado <strong>{filtroEstado}</strong>.
            </Alert>
          ) : (
            <Table hover responsive>
              <thead className="table-light">
                <tr>
                  <th>Consecutivo</th>
                  <th>Tipo</th>
                  <th>Fecha</th>
                  <th className="text-end">Monto</th>
                  <th className="text-center">Intentos</th>
                  <th>Último error</th>
                  <th className="text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c._id}>
                    <td><strong>{c.consecutivo}</strong></td>
                    <td><Badge bg="secondary">{c.tipoComprobante.replace(/_/g, ' ')}</Badge></td>
                    <td>{new Date(c.fecha).toLocaleDateString('es-CO')}</td>
                    <td className="text-end fw-bold">{formatoCOP(c.valorTotal)}</td>
                    <td className="text-center">
                      <Badge bg={c.intentosSincronizacion >= 5 ? 'danger' : 'warning'}>
                        {c.intentosSincronizacion}
                      </Badge>
                    </td>
                    <td><small className="text-danger">{c.ultimoErrorSync || '—'}</small></td>
                    <td className="text-center">
                      <Button variant="outline-info" size="sm" className="me-1"
                        onClick={() => handleVerHistorial(c._id)}
                        title="Ver historial">Ver</Button>
                      <Button variant="outline-primary" size="sm" className="me-1"
                        onClick={() => handleForzar(c._id)}
                        disabled={processing === c._id}
                        title="Forzar reintento"><FaSync /></Button>
                      <Button variant="outline-success" size="sm"
                        onClick={() => abrirManual(c)}
                        title="Marcar como sincronizado manualmente"><FaCheck /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal historial */}
      <Modal show={showHist} onHide={() => setShowHist(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Historial de sincronización</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {historial && (
            <>
              <p><strong>Consecutivo:</strong> {historial.consecutivo}</p>
              <p><strong>Estado:</strong> {historial.syncStatus}</p>
              <p><strong>Intentos:</strong> {historial.intentosSincronizacion}</p>
              <p><strong>External ID:</strong> {historial.externalId || '—'}</p>
              <p><strong>Fecha sync:</strong> {historial.fechaSincronizacion || '—'}</p>
              <p><strong>Último error:</strong> <code>{historial.ultimoErrorSync || '—'}</code></p>
              <hr />
              <h6>Payload enviado:</h6>
              <pre className="bg-light p-2 small" style={{ maxHeight: '200px', overflow: 'auto' }}>
                {JSON.stringify(historial.payloadEnviado, null, 2)}
              </pre>
              <h6>Respuesta externa:</h6>
              <pre className="bg-light p-2 small" style={{ maxHeight: '200px', overflow: 'auto' }}>
                {JSON.stringify(historial.responseExterna, null, 2)}
              </pre>
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* Modal marcar manual */}
      <Modal show={showManual} onHide={() => setShowManual(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Marcar como sincronizado manualmente</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning" className="small py-2">
            Usa esto si registraste el comprobante manualmente en el software contable externo.
            Quedará marcado como SINCRONIZADO con el externalId que provees.
          </Alert>
          <Form.Group className="mb-3">
            <Form.Label>External ID (del software contable) *</Form.Label>
            <Form.Control type="text"
              value={manualForm.externalId}
              onChange={(e) => setManualForm({ ...manualForm, externalId: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Justificación *</Form.Label>
            <Form.Control as="textarea" rows={2}
              value={manualForm.justificacion}
              onChange={(e) => setManualForm({ ...manualForm, justificacion: e.target.value })}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowManual(false)}>Cancelar</Button>
          <Button variant="success" onClick={confirmarMarcarManual}>Marcar sincronizado</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ErroresSincronizacion;
